import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertTriangle, CheckCircle2, User, CreditCard } from "lucide-react";

/**
 * Merged Personal Details + PAN Verification step.
 *
 * Flow:
 *   1. Customer fills name + DOB + gender + PAN in one screen
 *   2. On submit, validates locally, then:
 *        a) PUT /api/customer/profile  → saves name, dob, gender, pan_number
 *        b) POST /api/customer/verify-pan → Digio call (server reads dob from DB)
 *   3. On success → calls onComplete()
 *      On name/dob mismatch → highlights the specific field with an inline error
 */
export default function PersonalAndPANStep({
  form,
  setForm,
  loading,
  onVerifyAndContinue,
  fieldErrors = {},
}) {
  const [touched, setTouched] = useState({});

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const localErrors = {};
  if (touched.name) {
    if (!form.name?.trim()) localErrors.name = "Full name is required";
    else if (form.name.trim().length < 3) localErrors.name = "Please enter your full name as per PAN card";
  }
  if (touched.dob) {
    if (!form.dob) localErrors.dob = "Date of birth is required";
    else {
      const age = new Date().getFullYear() - new Date(form.dob).getFullYear();
      if (age < 18) localErrors.dob = "You must be at least 18 years old";
      if (age > 80) localErrors.dob = "Please enter a valid date of birth";
    }
  }
  if (touched.gender && !form.gender) localErrors.gender = "Please select your gender";
  if (touched.pan_number) {
    if (!form.pan_number) localErrors.pan_number = "PAN number is required";
    else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test((form.pan_number || "").toUpperCase()))
      localErrors.pan_number = "Invalid PAN format. Example: ABCDE1234F";
  }

  // Merge backend-driven errors (mismatch highlighting) with local validation errors
  const errors = { ...localErrors, ...fieldErrors };

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched({ name: true, dob: true, gender: true, pan_number: true });
    // Re-run local validation synchronously
    const valid =
      form.name?.trim().length >= 3 &&
      !!form.dob &&
      !!form.gender &&
      /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test((form.pan_number || "").toUpperCase());
    if (!valid) return;
    onVerifyAndContinue();
  };

  const maxDob = new Date(new Date().setFullYear(new Date().getFullYear() - 18))
    .toISOString()
    .split("T")[0];

  const errStyles = (field) =>
    errors[field]
      ? "border-red-400 focus:border-red-500 bg-red-50/40"
      : "border-slate-200 focus:border-blue-500";

  return (
    <form onSubmit={handleSubmit} className="space-y-5 animate-fade-in">
      {/* Sub-header / instruction */}
      <p className="text-sm text-slate-600 -mt-2">
        Fill your details <span className="font-semibold">exactly as they appear on your PAN card</span> to avoid verification failure.
      </p>

      {/* Important warning banner */}
      <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-300 rounded-xl p-3">
        <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={2.2} />
        <p className="text-xs text-amber-900 leading-relaxed">
          <strong>Important:</strong> Enter your name and date of birth exactly as printed on your PAN card. Even small differences will cause verification to fail.
        </p>
      </div>

      {/* PERSONAL DETAILS section */}
      <div className="bg-slate-50/70 border border-slate-200 rounded-xl p-4 space-y-4">
        <div className="flex items-center gap-2 text-blue-900">
          <User className="h-4 w-4" strokeWidth={2.4} />
          <span className="text-sm font-bold tracking-tight">Personal Details</span>
        </div>

        {/* Name */}
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-600 font-semibold">
            Full Name <span className="text-red-500">*</span>
          </Label>
          <Input
            data-testid="name-input"
            value={form.name || ""}
            onChange={(e) => update("name", e.target.value.toUpperCase())}
            onBlur={() => setTouched((t) => ({ ...t, name: true }))}
            placeholder="As on PAN card (e.g. ASHUTOSH GAIKWAD)"
            className={`mt-2 bg-white h-12 ${errStyles("name")}`}
          />
          {errors.name ? (
            <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> {errors.name}
            </p>
          ) : (
            <p className="mt-1.5 text-[11px] text-slate-400">Enter in CAPITAL LETTERS exactly as on PAN</p>
          )}
        </div>

        {/* DOB */}
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-600 font-semibold">
            Date of Birth <span className="text-red-500">*</span>
          </Label>
          <Input
            data-testid="dob-input"
            type="date"
            value={form.dob || ""}
            onChange={(e) => update("dob", e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, dob: true }))}
            max={maxDob}
            className={`mt-2 bg-white h-12 ${errStyles("dob")}`}
          />
          {errors.dob ? (
            <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> {errors.dob}
            </p>
          ) : (
            <p className="mt-1.5 text-[11px] text-slate-400">Must match your PAN card date of birth</p>
          )}
        </div>

        {/* Gender */}
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-600 font-semibold">
            Gender <span className="text-red-500">*</span>
          </Label>
          <select
            data-testid="gender-select"
            value={form.gender || ""}
            onChange={(e) => update("gender", e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, gender: true }))}
            className={`mt-2 w-full h-12 rounded-md border bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${errStyles(
              "gender"
            )}`}
          >
            <option value="">Select gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
          {errors.gender && (
            <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> {errors.gender}
            </p>
          )}
        </div>
      </div>

      {/* PAN section */}
      <div className="bg-slate-50/70 border border-slate-200 rounded-xl p-4 space-y-4">
        <div className="flex items-center gap-2 text-blue-900">
          <CreditCard className="h-4 w-4" strokeWidth={2.4} />
          <span className="text-sm font-bold tracking-tight">PAN Card Details</span>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-600 font-semibold">
            PAN Number <span className="text-red-500">*</span>
          </Label>
          <Input
            data-testid="pan-input"
            value={form.pan_number || ""}
            onChange={(e) => update("pan_number", e.target.value.toUpperCase().slice(0, 10))}
            onBlur={() => setTouched((t) => ({ ...t, pan_number: true }))}
            placeholder="ABCDE1234F"
            maxLength={10}
            className={`mt-2 bg-white h-12 tracking-[3px] font-semibold uppercase ${errStyles("pan_number")}`}
          />
          {errors.pan_number ? (
            <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> {errors.pan_number}
            </p>
          ) : (
            <p className="mt-1.5 text-[11px] text-slate-400">10 character PAN — 5 letters, 4 numbers, 1 letter</p>
          )}
        </div>
      </div>

      {/* CTA */}
      <button
        type="submit"
        data-testid="verify-pan-btn"
        disabled={loading}
        className="btn-gradient w-full h-14 rounded-xl font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" /> Verifying PAN...
          </>
        ) : (
          <>
            <CheckCircle2 className="h-5 w-5" /> Verify PAN &amp; Continue
          </>
        )}
      </button>

      <p className="text-[11px] text-center text-slate-400">
        Your PAN details are verified securely via Digio. We do not share your PAN number.
      </p>
    </form>
  );
}
